import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { DebugApi } from "../../services/api/multi/debug";

const frontendSentryEnabled = Boolean(import.meta.env.VITE_SENTRY_DSN);

export default function ObservabilityDebugPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loadingKey, setLoadingKey] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [captureMessage, setCaptureMessage] = useState(
    "Manual frontend Sentry debug message",
  );

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await DebugApi.getStatus();
      setStatus(data);
      setError(null);
    } catch (e) {
      setError(
        e?.response?.data?.detail || e.message || "Failed to load status",
      );
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const runAction = async (key, fn) => {
    setLoadingKey(key);
    setResult(null);
    setError(null);
    try {
      const response = await fn();
      setResult(response?.data || { ok: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (key === "backend-crash" && e?.response?.status >= 500) {
        setResult({
          ok: true,
          note: "Expected 500 — check Sentry for ZeroDivisionError",
          status: e.response.status,
          detail,
        });
      } else {
        setError(
          typeof detail === "string"
            ? detail
            : detail
              ? JSON.stringify(detail)
              : e.message || "Request failed",
        );
      }
    } finally {
      setLoadingKey("");
    }
  };

  const triggerFrontendCrash = () => {
    throw new Error("Frontend Sentry debug: intentional crash");
  };

  const triggerFrontendCapture = () => {
    if (!frontendSentryEnabled) {
      setError("VITE_SENTRY_DSN is not set in the frontend build");
      return;
    }
    const eventId = Sentry.captureMessage(captureMessage, "error");
    setResult({
      ok: true,
      event_id: eventId,
      message: captureMessage,
      source: "frontend",
    });
  };

  return (
    <Box sx={{ p: 4, maxWidth: 900 }}>
      <Button onClick={() => navigate("/debug")} sx={{ mb: 2 }}>
        ← Back to Debug hub
      </Button>

      <Typography variant="h5" gutterBottom>
        Observability debug (Sentry + Prometheus)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Only <strong>service-multi-repricer</strong> and the frontend have
        Sentry/Prometheus wired today. Other backend services still use the
        shared custom logger → Loki only.
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Chip
          label={
            status?.sentry_configured
              ? `Backend Sentry: ON (${status?.sentry_environment})`
              : "Backend Sentry: OFF (no SENTRY_DSN)"
          }
          color={status?.sentry_configured ? "success" : "default"}
        />
        <Chip
          label={
            frontendSentryEnabled
              ? "Frontend Sentry: ON"
              : "Frontend Sentry: OFF (no VITE_SENTRY_DSN)"
          }
          color={frontendSentryEnabled ? "success" : "default"}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Backend (multi-repricer)
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button
            variant="contained"
            color="error"
            disabled={!!loadingKey}
            onClick={() =>
              runAction("backend-crash", DebugApi.triggerSentryCrash)
            }
          >
            Crash (1 / 0)
          </Button>
          <Button
            variant="outlined"
            disabled={!!loadingKey}
            onClick={() =>
              runAction("backend-log", DebugApi.triggerSentryLogError)
            }
          >
            Logger error + exc_info
          </Button>
          <Button
            variant="outlined"
            disabled={!!loadingKey}
            onClick={() =>
              runAction("backend-capture", () =>
                DebugApi.triggerSentryCaptureMessage(
                  "Manual backend Sentry debug message from dashboard",
                ),
              )
            }
          >
            capture_message
          </Button>
          <Button
            variant="outlined"
            disabled={!!loadingKey}
            onClick={() =>
              runAction("prometheus", DebugApi.triggerPrometheusTest)
            }
          >
            Prometheus test counter
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Frontend (browser)
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Button
              variant="contained"
              color="error"
              onClick={triggerFrontendCrash}
            >
              Throw client error
            </Button>
            <Button variant="outlined" onClick={triggerFrontendCapture}>
              captureMessage
            </Button>
          </Stack>
          <TextField
            label="Frontend capture message"
            value={captureMessage}
            onChange={(e) => setCaptureMessage(e.target.value)}
            fullWidth
            size="small"
          />
        </Stack>
      </Paper>
    </Box>
  );
}
